import React, { useState, useEffect } from 'react'

import MarketVolatilityGraph from './MarketVolatilityGraph'
import CurrencyInput from './CurrencyInput'
import { getDropdownData } from '../../../utils/utils'
import {
  sanitizeHTML,
  APIENDPOINT,
  getCWPEnvironmentForURL,
} from '../../../../shared/utils/common-utils'

const balanceBoolsToArray = (
  bonds,
  conservative,
  moderatelyConservative,
  moderate,
  moderatelyAggressive,
  aggressive,
  stocks
) => {
  let array = []
  if (bonds) {
    array.push('fixedIncome')
  }
  if (conservative) {
    array.push('conservative')
  }
  if (moderatelyConservative) {
    array.push('moderatelyConservative')
  }
  if (moderate) {
    array.push('moderate')
  }
  if (moderatelyAggressive) {
    array.push('moderatelyAggressive')
  }
  if (aggressive) {
    array.push('aggressive')
  }
  if (stocks) {
    array.push('equities')
  }
  return array
}

const accumulationPercentageCopy =
  'Annual savings as a percentage of beginning Hypothetical Account Value<sup>*</sup>'
const decumulationPercentageCopy = 'Percent to withdraw per year (pretax)'

export default function MarketVolatilitySection(props) {
  // We use $1,000,000 as a default value so that advisors can quickly bypass configuration
  // and populate the graph.
  const initialState = {
    currentAssets: '1000000',
    contributionRateDropdownValues: ['3', '4', '5', '6', '7'],
    contributionRate: '5',
    interestAdjustmentRateDropdownValues: ['0', '1', '2', '3', '4', '5', '6'],
    interestAdjustmentRate: '2',
    bondsSelected: false,
    conservativeSelected: false,
    moderatelyConservativeSelected: false,
    moderateSelected: true,
    moderatelyAggressiveSelected: false,
    aggressiveSelected: false,
    stocksSelected: false,
    submitted: false,
    displayBalanceError: false,
    displayValueError: false,
    currencyInputValid: true,
  }
  const [state, setState] = useState(() => {
    const savedState = localStorage.getItem(`${props.idPrefix}`)
    return savedState ? { ...initialState, ...JSON.parse(savedState) } : initialState
  })

  const env = getCWPEnvironmentForURL(window.location.href)
  const dropdownExcelUrl = `${
    APIENDPOINT(env).baseGallery
  }content/assets/ampmicrosites/retirement-income-guide/marketvolatilitydropdown.xlsx`

  useEffect(() => {
    localStorage.setItem(`${props.idPrefix}`, JSON.stringify(state))
  }, [state])

  useEffect(() => {
    const setDropdownData = async () => {
      const dropdownData = await getDropdownData(dropdownExcelUrl, props.idPrefix)
      setState(prevState => ({
        ...prevState,
        ...dropdownData,
      }))
    }

    setDropdownData()
  }, [])

  const handleStateChange = (name, value) => {
    setState(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = () => {
    let currentAssetsNumeric = Number(state.currentAssets)
    let valueValid = state.currentAssets.length > 0 && !Number.isNaN(currentAssetsNumeric)

    // Validate that at least one balance has been selected
    let balanceSelected =
      state.bondsSelected ||
      state.conservativeSelected ||
      state.moderatelyConservativeSelected ||
      state.moderateSelected ||
      state.moderatelyAggressiveSelected ||
      state.aggressiveSelected ||
      state.stocksSelected

    if (valueValid && balanceSelected && state.currentAssets.length !== 0) {
      handleStateChange('displayValueError', false)
      handleStateChange('displayBalanceError', false)
      handleStateChange('submitted', true)
    } else {
      // CurrencyInput does not display error message for an empty field
      // (so that the default state isn’t an error state).
      //
      // But we want an error message for this form submisison with field empty.
      //
      // So we handle the empty field case with a new error message.

      if (!state.currencyInputValid && state.currentAssets.length === 0) {
        handleStateChange('displayValueError', true)
      }

      if (!balanceSelected) {
        handleStateChange('displayBalanceError', true)
      }
    }
  }

  return (
    <div className='market-volatility-wrap pt-accumulation-experience u-sizeFull u-paddingTop10gu u-paddingBottom5gu u-pt-paddingVert0'>
      <div className='market-volatility-content pt-accumulation-experience-content  u-paddingVert10gu u-bgColorWhite u-sizeConstrained pt-break-between-avoid'>
        <div className='u-pt-sizeFull u-marginCenter'>
          <div
            className='accumulation-experience'
            dangerouslySetInnerHTML={{
              __html: sanitizeHTML(props.introduction),
            }}></div>
        </div>
        <div className='market-volatility-content-wrapper u-paddingTop7gu u-flexCol u-sizeFull'>
          <div className='market-volatility-graph-controls'>
            <label
              htmlFor={
                props.accumulation ? 'accumulation-current-assets' : 'distribution-current-assets'
              }
              className='u-textSizeMsr u-marginBottom1gu u-textColorBrand Form-label'>
              Starting hypothetical value
            </label>
            <CurrencyInput
              name='accumulation-current-assets'
              value={state.currentAssets}
              valueChanged={value => handleStateChange('currentAssets', value)}
              isValid={state.currencyInputValid}
              validChanged={value => handleStateChange('currencyInputValid', value)}
              id={
                props.accumulation ? 'accumulation-current-assets' : 'distribution-current-assets'
              }
            />
            {state.displayValueError && (
              <p className='u-sizeFull u-textColorAlert'>Please enter a numeric value.</p>
            )}
            <p className='download-investment-balance-text u-textSizeMsr u-marginTop3gu u-marginBottom2gu u-textColorBrand Form-label'>
              **Investment Balance
            </p>
            <div className='market-volatility-button-wrap u-paddingBottom4gu u-flex u-flexWrap'>
              <label
                className={`form-control Chip u-marginTop3gu u-marginRight3gu u-paddingVert2gu u-border ${
                  state.bondsSelected ? 'u-bgColorBrand pt-button-text-color' : 'u-bgColorWhite'
                }`}>
                <input
                  type='checkbox'
                  className='u-posAbsolute'
                  checked={state.bondsSelected}
                  required
                  onChange={() => handleStateChange('bondsSelected', !state.bondsSelected)}
                />
                100% Bonds
              </label>

              <label
                className={`form-control Chip u-marginTop3gu u-marginRight3gu u-paddingVert2gu u-border ${
                  state.conservativeSelected
                    ? 'u-bgColorBrand pt-button-text-color'
                    : 'u-bgColorWhite'
                }`}>
                <input
                  type='checkbox'
                  className='u-posAbsolute'
                  checked={state.conservativeSelected}
                  required
                  onChange={() =>
                    handleStateChange('conservativeSelected', !state.conservativeSelected)
                  }
                />
                Conservative
              </label>

              <label
                className={`form-control Chip u-marginTop3gu u-marginRight3gu u-paddingVert2gu u-border ${
                  state.moderatelyConservativeSelected
                    ? 'u-bgColorBrand  pt-button-text-color'
                    : 'u-bgColorWhite'
                }`}>
                <input
                  type='checkbox'
                  className='u-posAbsolute'
                  checked={state.moderatelyConservativeSelected}
                  required
                  onChange={() =>
                    handleStateChange(
                      'moderatelyConservativeSelected',
                      !state.moderatelyConservativeSelected
                    )
                  }
                />
                Moderately Conservative
              </label>

              <label
                className={`form-control Chip u-marginTop3gu u-marginRight3gu u-paddingVert2gu u-border ${
                  state.moderateSelected ? 'u-bgColorBrand  pt-button-text-color' : 'u-bgColorWhite'
                }`}>
                <input
                  type='checkbox'
                  className='u-posAbsolute'
                  checked={state.moderateSelected}
                  required
                  onChange={() => handleStateChange('moderateSelected', !state.moderateSelected)}
                />
                Moderate
              </label>

              <label
                className={`form-control Chip u-marginTop3gu u-marginRight3gu u-paddingVert2gu u-border ${
                  state.moderatelyAggressiveSelected
                    ? 'u-bgColorBrand  pt-button-text-color'
                    : 'u-bgColorWhite'
                }`}>
                <input
                  type='checkbox'
                  className='u-posAbsolute'
                  checked={state.moderatelyAggressiveSelected}
                  required
                  onChange={() =>
                    handleStateChange(
                      'moderatelyAggressiveSelected',
                      !state.moderatelyAggressiveSelected
                    )
                  }
                />
                Moderately Aggressive
              </label>

              <label
                className={`form-control Chip u-marginTop3gu u-marginRight3gu u-paddingVert2gu u-border ${
                  state.aggressiveSelected
                    ? 'u-bgColorBrand  pt-button-text-color'
                    : 'u-bgColorWhite'
                }`}>
                <input
                  type='checkbox'
                  className='u-posAbsolute'
                  checked={state.aggressiveSelected}
                  required
                  onChange={() =>
                    handleStateChange('aggressiveSelected', !state.aggressiveSelected)
                  }
                />
                Aggressive
              </label>

              <label
                className={`form-control Chip u-marginTop3gu u-marginRight3gu u-paddingVert2gu u-border ${
                  state.stocksSelected ? 'u-bgColorBrand  pt-button-text-color' : 'u-bgColorWhite'
                }`}>
                <input
                  type='checkbox'
                  className='u-posAbsolute'
                  checked={state.stocksSelected}
                  required
                  onChange={() => handleStateChange('stocksSelected', !state.stocksSelected)}
                />
                100% Stocks
              </label>
              {state.displayBalanceError && (
                <p className='u-sizeFull u-textColorAlert'>Please select an investment balance.</p>
              )}
            </div>
            <div className='label-container'>
              <div className='download-label-wrapper'>
                <label
                  className='Form-label download-label u-textSizeMsr u-textColorBrand u-marginBottom3gu u-textPrimary'
                  htmlFor={`${props.idPrefix}-contribution-rate`}>
                  <span
                    className='u-textColorBrand'
                    dangerouslySetInnerHTML={{
                      __html: props.accumulation
                        ? accumulationPercentageCopy
                        : decumulationPercentageCopy,
                    }}></span>
                </label>
                <p
                  className={`download-label label-value u-pt-block u-hidden u-textSizeMsr u-textBold`}>
                  {Number(state.contributionRate)}%
                </p>
                <div className='u-sizeFull Dropdown u-pt-hidden u-marginBottom4gu'>
                  <select
                    value={state.contributionRate}
                    onChange={e => {
                      handleStateChange('contributionRate', e.target.value)
                    }}
                    className='Dropdown-menu'
                    id={`${props.idPrefix}-contribution-rate`}>
                    {state.contributionRateDropdownValues.map(value => {
                      return (
                        <option key={value} value={value}>
                          {value}%
                        </option>
                      )
                    })}
                  </select>
                </div>
              </div>
              <div className='download-label-wrapper u-marginLeft4gu'>
                <label
                  className='download-label u-flexWrap Form-label u-textSizeMsr u-textColorBrand u-marginBottom3gu u-textPrimary'
                  htmlFor={`${props.idPrefix}-interest-adjustment-rate`}>
                  Adjusted for impact of inflation
                </label>
                <p
                  className={`u-pt-block download-label u-textSizeMsr u-hidden  u-textBold u-sizeFull`}>
                  {Number(state.interestAdjustmentRate)}%
                </p>
                <div className='u-sizeFull u-pt-hidden Dropdown'>
                  <select
                    value={state.interestAdjustmentRate}
                    onChange={e => handleStateChange('interestAdjustmentRate', e.target.value)}
                    className='Dropdown-menu'
                    id={`${props.idPrefix}-interest-adjustment-rate`}>
                    {state.interestAdjustmentRateDropdownValues.map(value => {
                      return (
                        <option key={value} value={value}>
                          {value}%
                        </option>
                      )
                    })}
                  </select>
                </div>
              </div>
            </div>
            <button
              className='u-pt-hidden u-sizeFull u-padding1gu Button Button--primary u-marginVert4gu see-results-btn'
              onClick={handleSubmit}
              data-analytics={
                props.accumulation
                  ? 'RIG-accumulation-see-results-button'
                  : 'RIG-distribution-see-results-button'
              }>
              See Results
            </button>
          </div>
          <div className='download-graph-hidden u-sizeFull'>
            <figure>
              <MarketVolatilityGraph
                submitted={state.submitted}
                initialBalance={Number(state.currentAssets)}
                interestAdjustmentRate={Number(state.interestAdjustmentRate)}
                contributionRate={
                  props.accumulation
                    ? Number(state.contributionRate)
                    : -1 * Number(state.contributionRate)
                }
                graphHeading={props.description}
                investmentBalanceArray={balanceBoolsToArray(
                  state.bondsSelected,
                  state.conservativeSelected,
                  state.moderatelyConservativeSelected,
                  state.moderateSelected,
                  state.moderatelyAggressiveSelected,
                  state.aggressiveSelected,
                  state.stocksSelected
                )}
              />
            </figure>
          </div>
          {/* This graph is for PDF printing only. the way the highcharts scales is dependent on a window resize,
              which the pdf workflow doesn't do. So without it the graph wasn't resizing and it was getting cut off.
          */}
          <div className='market-volatility-graph-wrapper'>
            <figure>
              <MarketVolatilityGraph
                submitted={state.submitted}
                initialBalance={Number(state.currentAssets)}
                interestAdjustmentRate={Number(state.interestAdjustmentRate)}
                contributionRate={
                  props.accumulation
                    ? Number(state.contributionRate)
                    : -1 * Number(state.contributionRate)
                }
                graphHeading={props.description}
                investmentBalanceArray={balanceBoolsToArray(
                  state.bondsSelected,
                  state.conservativeSelected,
                  state.moderatelyConservativeSelected,
                  state.moderateSelected,
                  state.moderatelyAggressiveSelected,
                  state.aggressiveSelected,
                  state.stocksSelected
                )}
              />
            </figure>
          </div>
        </div>
      </div>
    </div>
  )
}
